openapi: 3.0.0
servers:
  - url: https://{API_SERVER_DOMAIN}.directcloud.jp
    variables:
      API_SERVER_DOMAIN:
        default: api

info:
  version: 1.2.0
  title: DirectCloud API Reference for 3rd Party
  description: |
    ### この文書はDirectCloudサービスと3rd Partyアプリケーションまたはウェブサイトを連動するために開発されたAPI案内文書です。

    - サービス安定化のため、開発APIサーバーと運営APIサーバーを分けて運営しておりますので、ご参考ください。

    - 文書の**{API_SERVER_DOMAIN}**に使われるドメインは別途案内します。

    ## Requests
    ### `lang` パラメータ
    * このパラメータはheader, query, formから抽出されるため、いずれか一つの方法をお選びてください。

    * エラー応答はクライアント要請に含まれている`lang`パラメータに依存するため、必ず送らなければなりません。

    ### アプリケーションからのAPI呼び出し
    DirectCloud APIはhttp標準方式を使います。
    - 例)フォルダーリストを取得するには次のようなHTTP要請を送ります。
      ```shell
      GET https://{API_SERVER_DOMAIN}/openapp/v1/folders/index
      ```
    <br>
    インタネットブラウザ以外のPCおよびモバイルアプリケーションからAPIを呼び出すとき、すべての要請に次のラメータがHTTP Headerに含まなれていければなりません。
    * 예) Headerに含まれるパラメータ
      |パラメータ名|値|必修|説明|例|
      |---------|-|--|--|-|
      device_id|String|Y|デバイス識別キー|ABCDEF-G123-4H56-7890-ACDEFGHIJ71
      device_name|String|Y|デバイス名|iPhone Simulator
      os_version|String|Y|デバイスOS名|iOS 7.0.3
      app_version|String|Y|APIを呼び出すAPP情報|DirectBox 1.0.0

    * 例) デバイス別

      device_type|device_id|device_name|os_version|app_version
      -|-|-|-|-
      PC Agent|49ed2c8c13xxx|DESKTOP-XXX|Windows 10|DirectBox 2.0.14
      IOS APP|D09D900B-xxxx|iPhone|iOS 10.3.2|DirectCloud 1.4.21.8
      Android APP|3548880620999|SM-N910K|Android+22|DirectBox+2.0.14

    ## Responses
    API応答はJSON形式を使います。

    ### `status_code`
    * 基本的にすべての応答の`status_code`は`200`です。

    * 応答の処理結果は`success`で判断できます。
      * 応答が成功した場合success値がtrueです。
      * 応答が失敗した場合success値がfalseです。

      **応答が成功した場合**
      ```json
      {
        "success": true,           // 応答が成功したか
        "operation_authority": {}, // 認証および権限情報
        "data": {}                 // 成功した応答情報
      }
      ```

      **応答が失敗した場合**
      ```json
      {
        "success": false,       // 応答が成功したか
        "all": "error message", // エラーメッセージ
        "code": "error code"    // エラーコード
      }
      ```

    <br><details>
    <summary>文書の変更履歴をもっと見る</summary>

    ### 文書変更履歴
    - **2021-02-02** 最初の作成
    - **2021-07-22**
      - フォルダ情報、フォルダ移動の追加
        - GET &nbsp;/openapp/v1/folders/index/{node}/{dir_seq}
        - POST /openapp/v1/folders/move/{node}
      - ファイル情報、ファイル移動の追加
        - GET &nbsp;/openapp/v1/files/index/{node}/{file_seq}
        - POST /openapp/v1/files/move/{node}
    - **2021-11-30**
      -  フォルダ容量情報追加
        -  GET /openapp/v1/folders/quota/{node}

    </detail>
    ---

security:
  - application:
      - read

tags:
  - name: Auth
    description: 認証API
  - name: Folder
    description: フォルダAPI
  - name: File
    description: ファイルAPI
  - name: Link
    description: リンクAPI
  - name: Thumbnail
    description: サムネール
  - name: Viewer
    description: ビューアーAPI
  - name: SSO
    description: SSO API
  - name: Webhook
    description: |
      Webhook API

      ## Webhookの制限事項

      - 登録可能なWebhookは**最大1000個/1会社ID**までです。
      - **管理者とフォルダの副管理者**のみWebhookの登録が可能です。
      - Webhook通知は、受信に失敗した場合最大2週間リトライされ、それ以後は削除されます。

      ___

      ## Webhookのペイロード

      イベントが発生したときに転送されるリクエストの内容です。

      > `from`フィールドには、移動またはコピー元フォルダの情報が含まれ、移動イベントとコピーイベントのペイロードのみに使用されます。

      ```
      POST {{webhook_url}}
      Authorization: Bearer {{auth_token}}
      Content-Type: application/json

      {
        "notification_id": "c4o7hfptnnb5qtgnosqg",
        "type": "outgoing-webhook",
        "trigger": "file-moved",
        "company_code": "sangilworld2",
        "user_id": "dev1",
        "username": "開発者1",
        "source": {
            "type": "file",
            "file_seq": 2995252,
            "dir_seq": 521148,
            "node": "1{24wk",
            "name": "test.pdf",
            "size": 7346,
            "dir_path": "/共有/webhookTest"
        },
        "from": {
            "dir_seq": 518303,
            "node": "1{2hpg",
            "dir_path": "/共有/uploadTest"
        },
        "webhook": {
            "dir_seq": 521148,
            "user_seq": 51211,
            "url": "http://dnotifier.private.directcloud.jp:1203/webhook",
            "triggers": [
                "file-uploaded",
                "file-deleted"
            ],
            "created_at": 1628582855,
            "modified_at": 1628582965
        },
        "timestamp": 1630566591
      }
      ```
      | Payload Detail | type | Description |
      | :---------------: | :--------------: | :--------------: |
      | notification_id | string | お知らせの固有ID |
      | type | string | type |
      | trigger | string | イベントトリガー |
      | company_code | string | 会社ID |
      | user_id   | string | ユーザーID |
      | username | string | ユーザー名 |
      | source | object(NotificationObject) | イベント対象情報 |
      | destination | object | ファイル・フォルダの名前変更、移動、コピー実行後の情報 |
      | webhook  | object (Webhook) | Webhook |
      | timestamp  | integer | イベント発生時間(UNIX-TIMESTAMP) |

      ___

      ## Webhookのイベントトリガー

      Webhook通知対象のイベントトリガーリストです。

      |Trigger|Description|
      |:--------------- |:--------------|
      | file-uploaded   | ファイルがアップロードされました。|
      | file-downloaded | ファイルをダウンロードしました。|
      | file-deleted    | ファイルが削除されました。|
      | file-restored   | ファイルを復旧しました。|
      | file-copied     | ファイルをコピーしました。|
      | file-renamed    | ファイル名を変更しました。|
      | file-moved      | ファイルを移動しました。|
      | folder-created  | フォルダを作成しました。|
      | folder-renamed  | フォルダ名を変更しました。|
      | folder-restored | フォルダを復旧しました。|
      | folder-deleted  | フォルダを削除しました。|
      | folder-copied   | フォルダをコピーしました。|
      | folder-moved    | フォルダを移動しました。|

  - name: WebhookPayload
    x-displayName: Webhook Payload
    description: |
      <SchemaDefinition schemaRef="#/components/schemas/WebhookPayload" />

x-tagGroups:
  - name: APIs
    tags:
      - Auth
      - Folder
      - File
      - Link
      - Thumbnail
      - Viewer
      - SSO
      - Webhook
  - name: Models
    tags:
      - WebhookPayload

paths:
  /openapi/jauth/token:
    $ref: "./path/auth.yaml#/Token"

  /openapi/jauth/access_token:
    $ref: "./path/auth.yaml#/AccessToken"

  /openapi/jauth/tokenExpire:
    $ref: "./path/auth.yaml#/TokenExpire"

  /openapp/v1/folders/index/{node}:
    $ref: "./path/folder.yaml#/FoldersIndexNode"

  /openapp/v1/folders/index/{node}/{dir_seq}:
    $ref: "./path/folder.yaml#/FoldersIndexNodeDirSeq"

  /openapp/v1/folders/create/{node}:
    $ref: "./path/folder.yaml#/FoldersCreateNode"

  /openapp/v1/folders/rename/{node}:
    $ref: "./path/folder.yaml#/FoldersRenameNode"

  /openapp/v1/folders/delete/{node}:
    $ref: "./path/folder.yaml#/FoldersDeleteNode"

  /openapp/v1/folders/move/{node}:
    $ref: "./path/folder.yaml#/FoldersMoveNode"

  /openapp/v1/folders/quota/{node}:
    $ref: "./path/folder.yaml#/FoldersQuotaNode"

  /openapp/v1/files/index/{node}:
    $ref: "./path/file.yaml#/FilesIndexNode"

  /openapp/v1/files/index/{node}/{file_seq}:
    $ref: "./path/file.yaml#/FilesIndexNodeFileSeq"

  /openapp/v1/files/upload/{node}:
    $ref: "./path/file.yaml#/FilesUploadNode"

  /openapp/v1/files/rename/{node}:
    $ref: "./path/file.yaml#/FilesRenameNode"

  /openapp/v1/files/download/{node}:
    $ref: "./path/file.yaml#/FilesDownloadNode"

  /openapp/v1/files/compress/{node}:
    $ref: "./path/file.yaml#/getFilesCompressNode"

  /openapp/v1/files/delete/{node}:
    $ref: "./path/file.yaml#/FilesDeleteNode"

  /openapp/v1/files/move/{node}:
    $ref: "./path/file.yaml#/FilesMoveNode"

  /openapp/v1/files/search/{node}:
    $ref: "./path/file.yaml#/FilesSearchNode"

  /openapp/v1/links/create:
    $ref: "./path/link.yaml#/LinksCreate"

  /openapp/v1/thumbnails/compress/{node}:
    $ref: "./path/thumbnail.yaml#/getThumbnailsCompressNode"

  /openapp/v1/viewer/create:
    $ref: "./path/viewer.yaml#/ViewerCreate"

  /openapi/sso/init:
    $ref: "./path/sso.yaml#/Init"

  /openapi/sso/login:
    $ref: "./path/sso.yaml#/Login"

  /openapi/sso/status:
    $ref: "./path/sso.yaml#/Status"

  /openapp/v1/webhooks:
    $ref: "./path/webhook.yaml#/Webhooks"

  /openapp/v1/webhooks/{node}:
    $ref: "./path/webhook.yaml#/Webhook"

components:
  schemas:
    WebhookPayload:
      $ref: './schemas/webhook.yaml#/WebhookPayload'
  securitySchemes:
    AccessToken:
      type: apiKey
      name: access_token
      in: header
      description: |
        Enter your Access Token in the format.
        Access Token を入力します。
